import { useEffect, useMemo, useState } from "react";
import { Pencil, Save, X } from "lucide-react";

import CustomInputNumber from "@/components/desktop/form/input/input-number/CustomInputNumber.jsx";
import styles from "./editable-number-cell.module.scss";

function toNumberOrNull(value) {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	const parsedNumber = Number(value);
	return Number.isFinite(parsedNumber) ? parsedNumber : null;
}

export default function EditableNumberCell({
	value,
	row,
	fieldName = "value",
	beforeSave,
	onSave,
	getRecordId = (originalRow) => originalRow?.id,
	displayFormatter,
	disabled = false,
	className = "",
	readButtonTitle = "Editar valor",
	savingText = "Guardando...",
	cancelText = "Cancelar",
	saveText = "Guardar",
	placeholder = "",
	required = false,
	align = "left",
}) {
	const recordId = useMemo(() => getRecordId?.(row?.original), [getRecordId, row]);
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(toNumberOrNull(value));
	const [isSaving, setIsSaving] = useState(false);
	const [errorText, setErrorText] = useState("");

	useEffect(() => {
		if (!isEditing) {
			setInputValue(toNumberOrNull(value));
		}
	}, [value, isEditing]);

	const beginEdit = () => {
		if (disabled || isSaving) {
			return;
		}

		setErrorText("");
		setIsEditing(true);
	};

	const cancelEdit = () => {
		setInputValue(toNumberOrNull(value));
		setErrorText("");
		setIsEditing(false);
	};

	const persistChange = async () => {
		if (disabled || isSaving) {
			return;
		}

		setErrorText("");
		const normalizedValue = toNumberOrNull(inputValue);

		if (required && normalizedValue === null) {
			setErrorText("Este campo es requerido");
			return;
		}

		if (!recordId || typeof onSave !== "function") {
			cancelEdit();
			return;
		}

		try {
			if (typeof beforeSave === "function") {
				const shouldContinue = await beforeSave({
					recordId,
					originalRow: row?.original,
					fieldName,
					newValue: normalizedValue,
				});

				if (!shouldContinue) {
					return;
				}
			}

			setIsSaving(true);
			await onSave({
				recordId,
				originalRow: row?.original,
				fieldName,
				newValue: normalizedValue,
			});
			setIsEditing(false);
		} catch (error) {
			setErrorText(error?.message || "No se pudo guardar");
		} finally {
			setIsSaving(false);
		}
	};

	if (!isEditing) {
		const textToShow = typeof displayFormatter === "function" ? displayFormatter(value) : (value ?? "—");
		const alignmentClassName = align === "right" ? styles.alignRight : "";

		return (
			<div className={`${styles.wrapper} ${alignmentClassName} ${className}`}>
				<button type="button" onClick={beginEdit} title={readButtonTitle} className={`${styles.readButton} ${alignmentClassName}`} disabled={disabled}>
					<span className={styles.readText}>{textToShow}</span>
					<Pencil className={styles.readIcon} aria-hidden="true" />
				</button>
			</div>
		);
	}

	const alignmentClassName = align === "right" ? styles.alignRight : "";

	return (
		<div className={`${styles.wrapper} ${alignmentClassName} ${className}`}>
			<div className={`${styles.inlineEdit} ${alignmentClassName}`}>
				<CustomInputNumber value={inputValue} onChange={setInputValue} placeholder={placeholder} size="sm" col={12} autoFocus />
				<div className={styles.actions}>
					<button type="button" className={styles.cancelButton} onClick={cancelEdit} disabled={isSaving}>
						<X className={styles.actionIcon} aria-hidden="true" />
						<span>{cancelText}</span>
					</button>
					<button type="button" className={styles.saveButton} onClick={persistChange} disabled={isSaving || disabled}>
						<Save className={styles.actionIcon} aria-hidden="true" />
						<span>{isSaving ? savingText : saveText}</span>
					</button>
				</div>
				{errorText ? <div className={styles.errorText}>{errorText}</div> : null}
			</div>
		</div>
	);
}
